import { type FC, useEffect, useState } from 'react';

import { t, T } from '@web-solutions/module-localization';
import Analytics from '@web-solutions/module-analytics';
import { EVENT_ACTION } from '@web-solutions/core/constants/general';
import { Modal, Title, Button } from '@web-solutions/core/ui-elements';

import { tKey } from '../../constants'
import { useSpecialOfferTimer, useNoFundsTimer } from '../../hooks'
import type { SpecialOfferBaseProps } from '../../types'

import styles from './style.module.scss';

interface SpecialOfferExpiredProps extends Pick<SpecialOfferBaseProps, 'mode'> {
  isSOOpen: boolean,
  onSOClose: () => void;
}

export const SpecialOfferExpired: FC<SpecialOfferExpiredProps> = ({
  mode,
  isSOOpen,
  onSOClose,
}) => {
  const [isSOOpenedTillEnd, setIsSOOpenedTillEnd] = useState(false)

  const { ended: specialOfferEnded } = useSpecialOfferTimer();
  const { ended: noFundsEnded } = useNoFundsTimer();

  const isNoFundsMode = mode === 'no_funds'
  const isSOEnded = isNoFundsMode ? noFundsEnded : specialOfferEnded

  const [expiredClosed, setExpiredClosed] = useState(() => isSOEnded);

  const isExpiredShown = isSOEnded && !expiredClosed && ((isSOOpenedTillEnd && isNoFundsMode) || !isNoFundsMode)

  useEffect(() => {
    if (!isSOEnded && !expiredClosed) {
      setIsSOOpenedTillEnd(isSOOpen)
    }
  }, [isSOEnded, expiredClosed, isSOOpen, setIsSOOpenedTillEnd])

  useEffect(() => {
    if (isExpiredShown) {
      Analytics.trackEvent('special_offer_expired', EVENT_ACTION.OPEN, { mode });
      setExpiredClosed(false);
      onSOClose();
    }
  }, [isExpiredShown, mode, setExpiredClosed, onSOClose]);

  const tExpiredKey = `${tKey}${isNoFundsMode ? `.${mode}` : ''}.expired`

  const handleCloseClick = () => {
    Analytics.trackEvent('special_offer_expired', EVENT_ACTION.CLOSE, { mode });
    setExpiredClosed(true);
  }

  return (
    <Modal
      isOpen={isExpiredShown}
      className={styles.modalContainer}
    >
      <Title level='h2' className={styles.title}>
        <T k={`${tExpiredKey}.title`} />
      </Title>
      {!isNoFundsMode &&
        <p className={styles.description}>
          <T k={`${tExpiredKey}.description`} />
        </p>
      }
      <Button
        title={t(`${tExpiredKey}.btn`)}
        onClick={handleCloseClick}
        className={styles.btn}
      />
    </Modal>
  );
};
